import { marker as _, marker } from '@colsen1991/ngx-translate-extract-marker';
import { FormProperty } from '@common/components/app-control/app-control.component';
import { WarningRule } from '@common/directives/warning-validator.directive';
import { ColumnSettings } from '@common/interfaces/column-settings.interface';
import { ValidationConstants } from '@common/validation-constants';
import { InvoiceNumberCellComponent } from './components/invoice-list/invoice-number-cell.component';
import { InvoiceListModel } from './models/invoice-list.model';
import { InvoiceAttachmentSaveModel } from './models/invoice-view.model';

export const invoiceListColumns: ColumnSettings<keyof InvoiceListModel>[] = [
    {
        field: 'internalInvoiceNumber',
        title: 'Internal Invoice No.',
        isLink: true,
        customRoute: {
            path: '/invoice',
            field: 'id'
        }
    },
    {
        field: 'number',
        title: 'Invoice No.',
        templateComponent: InvoiceNumberCellComponent,
        templateInputs: (row) => ({
            invoiceNumber: row.number,
            invoiceId: row.id,
            hasPdf: row.hasPdf
        })
    },
    { field: 'invoiceTypeName', title: 'Invoice Type', type: 'string', isSortable: false },
    {
        field: 'invoiceStatusId',
        title: 'Invoice Status',
        type: 'codelist',
        codelistName: 'InvoiceStatus',
        codelistNameInPill: true
    },
    { field: 'customerName', title: 'Customer', type: 'string' },
    {
        field: 'reference',
        title: 'Reference',
        isLink: true,
        customRoute: {
            field: 'referenceId',
            pathField: 'route',
            permissionField: 'permission'
        }
    },
    { field: 'vesselVersionName', title: 'Vessel Name', type: 'string' },
    { field: 'amount', title: 'Amount', type: 'number', format: 'n2' },
    {
        field: 'taxAmount',
        title: 'Tax Amount',
        type: 'number',
        format: 'n2'
    },
    {
        field: 'totalAmount',
        title: 'Total Amount',
        type: 'number',
        format: 'n2'
    },
    { field: 'currencyName', title: 'Currency', type: 'string', isSortable: false },
    { field: 'createdByName', title: 'Created By', type: 'string', isSortable: false },
    { field: 'sentForApprovalByName', title: 'Sent for Approval By', type: 'string', isSortable: false },
    { field: 'approvedByName', title: 'Approved By', type: 'string', isSortable: false },
    { field: 'sentToPoint', title: 'Sent to Point', type: 'boolean' }
];

export const invoiceAttachmentColumns: ColumnSettings<keyof InvoiceAttachmentSaveModel>[] = [
    { field: 'file', title: 'File', type: 'file', width: 25 },
    {
        field: 'attachmentTypeId',
        title: 'Attachment Type',
        type: 'codelist',
        codelistName: 'AttachmentType',
        codelistNameOnly: true,
        width: 10
    },
    {
        field: 'internal',
        title: 'Internal',
        type: 'boolean',
        width: 5
    },
    {
        field: 'createdById',
        title: 'Created By',
        type: 'codelist',
        codelistName: 'User',
        codelistFull: true,
        width: 15
    },
    { field: 'createdDate', title: 'Created Date', type: 'date', width: 15 },
    {
        field: 'lastModifiedById',
        title: 'Last Modified By',
        type: 'codelist',
        codelistName: 'User',
        codelistFull: true,
        width: 15
    },
    { field: 'lastModifiedDate', title: 'Last Modified Date', type: 'date', width: 15 }
];

export const invoiceAttachmentProperties: FormProperty<keyof InvoiceAttachmentSaveModel>[] = [
    {
        label: 'Attachment Type',
        name: 'attachmentTypeId',
        type: 'codelist',
        codelist: 'AttachmentType',
        required: true,
        colSpan: 3
    },
    {
        label: 'Internal',
        name: 'internal',
        type: 'boolean',
        colSpan: 1
    },
    {
        name: 'file',
        type: 'file',
        required: true,
        colSpan: 8
    }
];

export const getInvoiceFilterProperties = (filter: any): FormProperty[] => [
    {
        name: 'internalInvoiceNo',
        label: 'Internal Invoice No.',
        type: 'number',
        format: '#',
        decimals: 0,
        min: 0,
        max: ValidationConstants.DataTypeLimits.MaxBigInt,
        colSpan: 2,
        initialValue: filter?.internalInvoiceNo
    },
    {
        name: 'invoiceNo',
        label: 'Invoice No.',
        type: 'string',
        pattern: '^[0-9-]$',
        colSpan: 2,
        initialValue: filter?.invoiceNo
    },
    {
        name: 'invoiceTypeId',
        label: 'Invoice Type',
        type: 'codelist',
        codelist: 'InvoiceType',
        multi: true,
        colSpan: 2,
        initialValue: filter?.invoiceTypeId
    },
    {
        name: 'invoiceStatusId',
        label: 'Invoice Status',
        type: 'codelist',
        codelist: 'InvoiceStatus',
        multi: true,
        colSpan: 2,
        initialValue: filter?.invoiceStatusId
    },
    {
        name: 'contractNo',
        label: 'Contract No.',
        type: 'number',
        format: '#',
        decimals: 0,
        min: 0,
        max: ValidationConstants.DataTypeLimits.MaxBigInt,
        colSpan: 2,
        initialValue: filter?.contractNo
    },
    {
        name: 'vesselVisitNo',
        label: 'Vessel Visit No.',
        type: 'string',
        colSpan: 2,
        initialValue: filter?.vesselVisitNo
    },
    {
        name: 'vesselId',
        label: 'Vessel Name',
        type: 'codelist',
        codelist: 'Vessel',
        multi: true,
        colSpan: 2,
        initialValue: filter?.vesselId
    },
    {
        name: 'customerId',
        label: 'Customer',
        type: 'codelist',
        codelist: 'Customer',
        multi: true,
        colSpan: 2,
        initialValue: filter?.customerId
    },
    {
        name: 'createdByUserId',
        label: 'Created By',
        type: 'codelist',
        codelist: 'User',
        multi: true,
        colSpan: 2,
        initialValue: filter?.createdByUserId
    },
    {
        name: 'createdDate',
        label: 'Created Date',
        type: 'datetime',
        colSpan: 2,
        initialValue: filter?.createdDate
    },
    {
        name: 'approvedByUserId',
        label: 'Approved By',
        type: 'codelist',
        codelist: 'User',
        multi: true,
        colSpan: 2,
        initialValue: filter?.approvedByUserId
    },
    {
        name: 'approvalDate',
        label: 'Approval Date',
        type: 'datetime',
        colSpan: 2,
        initialValue: filter?.approvalDate
    },
    {
        name: 'sentToPoint',
        label: 'Sent to Point',
        type: 'boolean',
        colSpan: 2,
        initialValue: filter?.sentToPoint
    },
    {
        name: 'myInvoices',
        label: 'My Invoices',
        type: 'boolean',
        colSpan: 2,
        initialValue: filter?.myInvoices
    },
    {
        name: 'hasPdf',
        label: 'Has PDF',
        type: 'boolean',
        nullable: true,
        colSpan: 2,
        initialValue: null
    }
];

export const getPaymentFieldConfigs = () => ({
    paymentDueDays: {
        label: _('Payment Due Days'),
        type: 'number' as const,
        format: ValidationConstants.FinancialLimits.PaymentDueDayFormat,
        decimals: ValidationConstants.FinancialLimits.PaymentDueDayDecimals,
        min: ValidationConstants.FinancialLimits.MinPaymentDueDays,
        max: ValidationConstants.FinancialLimits.MaxPaymentDueDays,
        required: true
    }
});

export const getInvoiceDateValidator = (sourceId?: Date): WarningRule<Date>[] => {
    const rules: WarningRule<Date>[] = [];

    rules.push({
        condition: (value: Date) => value > new Date(),
        message: marker('Date exceeding max'),
        tooltip: marker('Selected Date is after current date.'),
        severity: 'warning'
    });

    return rules;
};
